import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe, Plus, RefreshCw, Trash2, Link2, CheckCircle2, AlertCircle,
  Clock, Shield, Copy, ExternalLink, ChevronRight,
  FileText, X, Activity, Zap, Route, Home, Pencil,
} from 'lucide-react';
import { getToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending:              { label: 'Pending Verification', color: 'text-amber-400',   bg: 'bg-amber-950/40 border-amber-800/40',   icon: Clock },
  verification_failed:  { label: 'Verification Failed',  color: 'text-red-400',     bg: 'bg-red-950/40 border-red-800/40',       icon: AlertCircle },
  verified:             { label: 'Verified',              color: 'text-sky-400',     bg: 'bg-sky-950/40 border-sky-800/40',       icon: CheckCircle2 },
  activation_pending:   { label: 'Activation Pending',    color: 'text-violet-400',  bg: 'bg-violet-950/40 border-violet-800/40', icon: Shield },
  active:               { label: 'Active',                color: 'text-emerald-400', bg: 'bg-emerald-950/40 border-emerald-800/40', icon: CheckCircle2 },
  failed:               { label: 'Failed',                color: 'text-red-400',     bg: 'bg-red-950/40 border-red-800/40',       icon: AlertCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span data-testid={`domain-status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.color}`}
    >
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={handleCopy} className="p-1 rounded text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors" title="Copy">
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function CnameInstructions({ domain }) {
  const subdomain = domain.includes('.') ? domain.split('.')[0] : domain;
  return (
    <div data-testid="cname-instructions" className="rounded-lg bg-zinc-950 border border-zinc-800 p-4">
      <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-3">Step 2: DNS Routing</p>
      <div className="space-y-2.5">
        <div className="flex items-start gap-2"><span className="text-xs text-zinc-500 w-14 flex-shrink-0 pt-0.5">Type</span><span className="text-sm font-mono text-zinc-200">CNAME</span></div>
        <div className="flex items-start gap-2"><span className="text-xs text-zinc-500 w-14 flex-shrink-0 pt-0.5">Name</span><span className="text-sm font-mono text-sky-300">{subdomain}</span><CopyButton text={subdomain} /></div>
        <div className="flex items-start gap-2"><span className="text-xs text-zinc-500 w-14 flex-shrink-0 pt-0.5">Target</span><span className="text-sm font-mono text-sky-300">proxy.missionassist.ai</span><CopyButton text="proxy.missionassist.ai" /></div>
      </div>
      <p className="text-xs text-zinc-500 mt-3">Point your domain to proxy.missionassist.ai via CNAME with Proxy enabled (orange cloud).</p>
    </div>
  );
}

function ActivationCheck({ domain: dom, headers }) {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const handleCheck = async () => {
    setChecking(true); setResult(null);
    try { const res = await fetch(`${API}/api/domains/${dom.id}/check-activation`, { method: 'POST', headers }); setResult(await res.json()); }
    catch (e) { setResult({ detail: e.message }); }
    finally { setChecking(false); }
  };
  return (
    <div data-testid="activation-check" className="rounded-lg bg-violet-950/30 border border-violet-800/30 px-4 py-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div><p className="text-xs text-violet-300 font-medium">Routing in progress</p><p className="text-xs text-zinc-500 mt-1">Configure the CNAME record above, then check activation.</p></div>
        <button data-testid="check-activation-btn" onClick={handleCheck} disabled={checking}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600/80 hover:bg-violet-500 text-white text-xs font-medium transition-colors disabled:opacity-50 flex-shrink-0">
          <RefreshCw className={`w-3.5 h-3.5 ${checking ? 'animate-spin' : ''}`} />{checking ? 'Checking...' : 'Check Activation'}
        </button>
      </div>
      {result && (
        <div data-testid="activation-result" className="rounded-md bg-zinc-950/60 px-3 py-2 space-y-1.5">
          <div className="flex items-center gap-2 text-xs"><span className="text-zinc-500">CNAME:</span>
            {result.cname_configured ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {result.cname_target}</span>
            : <span className="text-amber-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Not configured</span>}
          </div>
          <div className="flex items-center gap-2 text-xs"><span className="text-zinc-500">Ready:</span>
            {result.reachable ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ready for activation</span>
            : <span className="text-zinc-400">{result.detail || 'Not yet reachable'}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

const LIVE_STATUS = {
  live:        { color: 'bg-emerald-500', text: 'text-emerald-400', label: 'Live' },
  unreachable: { color: 'bg-red-500',     text: 'text-red-400',     label: 'Not reachable' },
  failing:     { color: 'bg-amber-500',   text: 'text-amber-400',   label: 'Failing' },
  timeout:     { color: 'bg-amber-500',   text: 'text-amber-400',   label: 'Timeout' },
};

function LiveCheckIndicator({ domain: dom, headers, onRefresh }) {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(dom.last_live_check || null);
  useEffect(() => { if (dom.last_live_check) setResult(dom.last_live_check); }, [dom.last_live_check]);
  useEffect(() => {
    if (!dom.live_check_in_progress) return;
    let attempts = 0;
    const interval = setInterval(() => { attempts++; if (attempts >= 10) { clearInterval(interval); return; } onRefresh(); }, 3000);
    return () => clearInterval(interval);
  }, [dom.live_check_in_progress, dom.id, onRefresh]);
  const handleCheck = async () => {
    setChecking(true);
    try { const res = await fetch(`${API}/api/domains/${dom.id}/live-check`, { headers }); const data = await res.json(); setResult(data); onRefresh(); }
    catch (e) { setResult({ status: 'unreachable', message: e.message }); }
    finally { setChecking(false); }
  };
  const isAutoChecking = dom.live_check_in_progress && !result;
  const statusCfg = result ? (LIVE_STATUS[result.status] || LIVE_STATUS.unreachable) : null;
  return (
    <div data-testid="live-check" className="rounded-lg bg-zinc-950/70 border border-zinc-800 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Activity className="w-4 h-4 text-zinc-500" /><span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Live Status</span>
          {isAutoChecking && <span data-testid="live-status-auto-checking" className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-400"><RefreshCw className="w-3 h-3 animate-spin" />Checking...</span>}
          {!isAutoChecking && result && <span data-testid="live-status-indicator" className={`inline-flex items-center gap-1.5 text-xs font-medium ${statusCfg.text}`}><span className={`w-2 h-2 rounded-full ${statusCfg.color} ${result.status === 'live' ? 'animate-pulse' : ''}`} />{statusCfg.label}</span>}
        </div>
        <button data-testid="live-check-btn" onClick={handleCheck} disabled={checking || isAutoChecking}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-medium hover:bg-zinc-700 hover:text-zinc-100 transition-colors disabled:opacity-50">
          <Zap className={`w-3.5 h-3.5 ${checking ? 'animate-pulse' : ''}`} />{checking ? 'Checking...' : 'Check'}
        </button>
      </div>
      {!isAutoChecking && result && (
        <div data-testid="live-check-details" className="mt-3 space-y-1.5">
          {result.message && <p className={`text-xs ${statusCfg.text}`}>{result.message}</p>}
          <div className="flex items-center gap-4 text-[11px] text-zinc-500">
            {result.response_time_ms != null && <span>Response: {result.response_time_ms}ms</span>}
            {result.checked_at && <span>Checked: {new Date(result.checked_at).toLocaleTimeString()}</span>}
            {result.http_status && <span>HTTP {result.http_status}</span>}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Add Route Modal ────────────────────────────────────────────
function AddRouteModal({ domainId, domainName, existingSlugs, onClose, onSaved, headers }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLP, setSelectedLP] = useState(null);
  const [slug, setSlug] = useState('');
  const [isRoot, setIsRoot] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/domains/${domainId}/mapping-options`, { headers });
        if (!res.ok) throw new Error('Failed to load options');
        const data = await res.json();
        setOptions(data.landing_pages || []);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [domainId, headers]);

  const handleSave = async () => {
    if (!selectedLP) return;
    const finalSlug = isRoot ? '' : slug.trim().toLowerCase();
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API}/api/domains/${domainId}/routes`, {
        method: 'POST', headers,
        body: JSON.stringify({ slug: finalSlug, landing_page_id: selectedLP }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create route');
      onSaved(); onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const hasRootRoute = existingSlugs.includes('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="add-route-modal" className="bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <h3 className="text-base font-semibold text-zinc-100">Add Route</h3>
          <button onClick={onClose} className="p-1 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          {error && <div className="px-3 py-2 rounded-lg bg-red-950/40 border border-red-800/40 text-red-300 text-xs">{error}</div>}

          {/* Slug Input */}
          <div>
            <label className="block text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">Path / Slug</label>
            {!hasRootRoute && (
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input data-testid="root-route-toggle" type="checkbox" checked={isRoot} onChange={e => setIsRoot(e.target.checked)}
                  className="rounded border-zinc-600 bg-zinc-800 text-sky-500 focus:ring-sky-500/30" />
                <span className="text-sm text-zinc-300">Homepage (root route)</span>
                <Home className="w-3.5 h-3.5 text-zinc-500" />
              </label>
            )}
            {!isRoot && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-500 font-mono">{domainName}/</span>
                <input data-testid="slug-input" type="text" value={slug} onChange={e => setSlug(e.target.value.replace(/[^a-z0-9-]/g, ''))}
                  placeholder="offer-1" maxLength={128}
                  className="flex-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 font-mono placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-sky-500/50" />
              </div>
            )}
            {isRoot && (
              <div className="px-3 py-2 rounded-lg bg-sky-950/30 border border-sky-800/20 text-sky-300 text-xs">
                This route will serve as the homepage when visitors go to <span className="font-mono">{domainName}/</span>
              </div>
            )}
          </div>

          {/* LP Selector */}
          {loading ? (
            <div className="text-center text-zinc-500 text-sm py-8">Loading landing pages...</div>
          ) : options.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">No published landing pages available</p>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">Landing Page</label>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {options.map(lp => (
                  <button key={lp.landing_page_id} data-testid={`lp-option-${lp.landing_page_id}`}
                    onClick={() => setSelectedLP(lp.landing_page_id)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                      selectedLP === lp.landing_page_id
                        ? 'border-sky-500/50 bg-sky-950/30 text-zinc-100'
                        : 'border-zinc-800 bg-zinc-950/50 text-zinc-300 hover:border-zinc-700 hover:bg-zinc-800/40'
                    }`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium flex-1">{lp.name}</span>
                      {lp.active_version_number && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-950/50 border border-emerald-800/40 text-emerald-400">v{lp.active_version_number}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">Publishing a new LP version updates the served content automatically.</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-800">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-400 text-sm hover:bg-zinc-800 transition-colors">Cancel</button>
          <button data-testid="save-route-btn" onClick={handleSave} disabled={!selectedLP || (!isRoot && !slug.trim()) || saving}
            className="px-5 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Saving...' : 'Add Route'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Route Table ────────────────────────────────────────────────
function RouteTable({ domain: dom, headers, onRefresh }) {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const canManage = ['verified', 'activation_pending', 'active'].includes(dom.status);

  const fetchRoutes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/domains/${dom.id}/routes`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setRoutes(data.routes || []);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, [dom.id, headers]);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const handleDelete = async (routeId) => {
    if (!window.confirm('Remove this route?')) return;
    setDeleting(routeId);
    try {
      await fetch(`${API}/api/domains/${dom.id}/routes/${routeId}`, { method: 'DELETE', headers });
      fetchRoutes(); onRefresh();
    } finally { setDeleting(null); }
  };

  const handleSaved = () => { fetchRoutes(); onRefresh(); };

  if (!canManage) return null;

  const hasRootRoute = routes.some(r => r.slug === '');
  const existingSlugs = routes.map(r => r.slug);

  return (
    <div data-testid="route-table" className="rounded-lg bg-zinc-950/70 border border-zinc-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Route className="w-4 h-4 text-zinc-500" />
          <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Routes</p>
          <span className="text-[10px] text-zinc-600">({routes.length})</span>
        </div>
        <button data-testid="add-route-btn" onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600/80 hover:bg-sky-500 text-white text-xs font-medium transition-colors">
          <Plus className="w-3.5 h-3.5" />Add Route
        </button>
      </div>

      {/* No homepage warning */}
      {!loading && !hasRootRoute && (
        <div data-testid="no-homepage-warning" className="mb-3 px-3 py-2.5 rounded-lg bg-amber-950/30 border border-amber-800/30">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-amber-300 font-medium">No homepage configured</p>
              <p className="text-[11px] text-amber-400/70 mt-0.5">
                Visitors to <span className="font-mono">{dom.domain}/</span> will see a 404 page. Add a root route to set a homepage.
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-zinc-500 text-sm py-6">Loading routes...</div>
      ) : routes.length === 0 ? (
        <div data-testid="no-routes" className="text-center py-6">
          <Route className="w-7 h-7 text-zinc-700 mx-auto mb-2" />
          <p className="text-zinc-500 text-xs">No routes configured</p>
          <p className="text-zinc-600 text-[11px] mt-1">Each route maps a URL path to a Landing Page</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {routes.map(route => (
            <div key={route.id} data-testid={`route-row-${route.slug || 'root'}`}
              className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg border border-zinc-800/60 bg-zinc-900/50 hover:bg-zinc-800/30 transition-colors group">
              {/* Slug */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {route.slug === '' ? (
                    <span className="inline-flex items-center gap-1 text-sm font-mono text-emerald-400">
                      <Home className="w-3.5 h-3.5" />/
                      <span className="text-[10px] font-sans text-emerald-500/70 ml-1">homepage</span>
                    </span>
                  ) : (
                    <span className="text-sm font-mono text-zinc-200">/{route.slug}</span>
                  )}
                </div>
              </div>
              {/* Arrow */}
              <ChevronRight className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
              {/* LP name */}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-zinc-300 truncate">{route.lp_name}</p>
                {route.lp_status === 'archived' && (
                  <span className="text-[10px] text-red-400">archived — will not serve</span>
                )}
              </div>
              {/* Actions */}
              <button data-testid={`delete-route-${route.slug || 'root'}`}
                onClick={() => handleDelete(route.id)} disabled={deleting === route.id}
                className="p-1.5 rounded-md text-zinc-600 hover:text-red-400 hover:bg-red-950/30 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-zinc-600 mt-3">Each slug maps to one Landing Page. Publishing a new version auto-updates the route.</p>

      {showModal && (
        <AddRouteModal
          domainId={dom.id} domainName={dom.domain} existingSlugs={existingSlugs}
          headers={headers} onClose={() => setShowModal(false)} onSaved={handleSaved}
        />
      )}
    </div>
  );
}


// ── Main Page ──────────────────────────────────────────────────
export default function DomainsSettingsPage() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState({});
  const [error, setError] = useState(null);

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
  };

  const fetchDomains = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/domains`, { headers });
      if (!res.ok) throw new Error('Failed to load domains');
      const data = await res.json();
      setDomains(data.domains || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDomains(); }, [fetchDomains]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setAdding(true); setError(null);
    try {
      const res = await fetch(`${API}/api/domains`, { method: 'POST', headers, body: JSON.stringify({ domain: newDomain.trim().toLowerCase() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add domain');
      setNewDomain(''); fetchDomains();
    } catch (e) { setError(e.message); }
    finally { setAdding(false); }
  };

  const handleVerify = async (domainId) => {
    setVerifying(v => ({ ...v, [domainId]: true }));
    try { await fetch(`${API}/api/domains/${domainId}/verify`, { method: 'POST', headers }); fetchDomains(); }
    catch (e) { setError(e.message); }
    finally { setVerifying(v => ({ ...v, [domainId]: false })); }
  };

  const handleDelete = async (domainId) => {
    if (!window.confirm('Remove this domain? This cannot be undone.')) return;
    try { await fetch(`${API}/api/domains/${domainId}`, { method: 'DELETE', headers }); fetchDomains(); }
    catch (e) { setError(e.message); }
  };

  return (
    <div data-testid="domains-settings-page" className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-xl bg-sky-950/50 border border-sky-800/30"><Globe className="w-5 h-5 text-sky-400" /></div>
          <h1 className="text-2xl font-semibold text-zinc-100 tracking-tight">Custom Domains</h1>
        </div>
        <p className="text-sm text-zinc-400 ml-12">Connect your own domains and map URL paths to Landing Pages.</p>
      </div>

      <form onSubmit={handleAdd} data-testid="add-domain-form" className="flex gap-3 mb-6">
        <input data-testid="domain-input" type="text" value={newDomain} onChange={(e) => setNewDomain(e.target.value)}
          placeholder="offers.yourcompany.com"
          className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500/50" />
        <button data-testid="add-domain-btn" type="submit" disabled={adding || !newDomain.trim()}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus className="w-4 h-4" />{adding ? 'Adding...' : 'Add Domain'}
        </button>
      </form>

      {error && (
        <div data-testid="domain-error" className="mb-4 px-4 py-2.5 rounded-lg bg-red-950/40 border border-red-800/40 text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-200 text-xs">dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="text-center text-zinc-500 py-16 text-sm">Loading domains...</div>
      ) : domains.length === 0 ? (
        <div data-testid="no-domains" className="text-center py-16 border border-dashed border-zinc-700 rounded-xl">
          <Globe className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">No custom domains yet</p>
          <p className="text-zinc-500 text-xs mt-1">Add a domain above to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {domains.map((dom) => (
            <DomainCard key={dom.id} domain={dom} headers={headers}
              verifying={verifying[dom.id]} onVerify={() => handleVerify(dom.id)}
              onDelete={() => handleDelete(dom.id)} onRefresh={fetchDomains} />
          ))}
        </div>
      )}
    </div>
  );
}

function DomainCard({ domain: dom, headers, verifying, onVerify, onDelete, onRefresh }) {
  const [expanded, setExpanded] = useState(
    dom.status === 'pending' || dom.status === 'verification_failed' ||
    ['verified', 'activation_pending', 'active'].includes(dom.status)
  );

  const showVerifyAction = ['pending', 'verification_failed'].includes(dom.status);
  const showDnsInstructions = ['pending', 'verification_failed'].includes(dom.status);

  return (
    <div data-testid={`domain-card-${dom.domain}`} className="rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-zinc-800/30 transition-colors" onClick={() => setExpanded(!expanded)}>
        <Globe className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        <span className="font-mono text-sm text-zinc-200 flex-1">{dom.domain}</span>
        <StatusBadge status={dom.status} />
        <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-zinc-800/60 pt-4 space-y-4">
          {/* DNS Instructions */}
          {showDnsInstructions && (
            <div data-testid="dns-instructions" className="rounded-lg bg-zinc-950 border border-zinc-800 p-4">
              <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-3">Step 1: DNS Verification</p>
              <div className="space-y-2.5">
                <div className="flex items-start gap-2"><span className="text-xs text-zinc-500 w-14 flex-shrink-0 pt-0.5">Type</span><span className="text-sm font-mono text-zinc-200">TXT</span></div>
                <div className="flex items-start gap-2"><span className="text-xs text-zinc-500 w-14 flex-shrink-0 pt-0.5">Name</span><span className="text-sm font-mono text-sky-300 break-all">{dom.verification_record}</span><CopyButton text={dom.verification_record} /></div>
                <div className="flex items-start gap-2"><span className="text-xs text-zinc-500 w-14 flex-shrink-0 pt-0.5">Value</span><span className="text-sm font-mono text-sky-300 break-all">{dom.verification_token}</span><CopyButton text={dom.verification_token} /></div>
              </div>
              <p className="text-xs text-zinc-500 mt-3">Add this TXT record in your DNS provider, then click Verify.</p>
            </div>
          )}

          {['verified', 'activation_pending'].includes(dom.status) && <CnameInstructions domain={dom.domain} />}

          {dom.failure_reason && (
            <div data-testid="failure-reason" className="rounded-lg bg-red-950/30 border border-red-800/30 px-4 py-3">
              <p className="text-xs text-red-300">{dom.failure_reason}</p>
            </div>
          )}

          {/* Route Table — replaces old single-LP mapping */}
          <RouteTable domain={dom} headers={headers} onRefresh={onRefresh} />

          {/* Activation Pending + Check */}
          {dom.status === 'activation_pending' && <ActivationCheck domain={dom} headers={headers} />}

          {/* Active: Live Check + External Link */}
          {dom.status === 'active' && (
            <>
              <LiveCheckIndicator domain={dom} headers={headers} onRefresh={onRefresh} />
              <div className="flex items-center gap-2 text-xs text-emerald-400">
                <ExternalLink className="w-3.5 h-3.5" />
                <a href={`https://${dom.domain}`} target="_blank" rel="noopener noreferrer" className="hover:underline">https://{dom.domain}</a>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            {showVerifyAction && (
              <button data-testid={`verify-btn-${dom.domain}`} onClick={onVerify} disabled={verifying}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-sky-600/80 hover:bg-sky-500 text-white text-xs font-medium transition-colors disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${verifying ? 'animate-spin' : ''}`} />{verifying ? 'Verifying...' : 'Verify'}
              </button>
            )}
            <button data-testid={`delete-btn-${dom.domain}`} onClick={onDelete}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-red-400 hover:border-red-800/50 text-xs transition-colors">
              <Trash2 className="w-3.5 h-3.5" />Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
